import { ThermalHotspot } from "~/pages/copilotMessage/types/copilot";
import {
  ISectionMaxValues,
  // ISectionMaxValues,
  SectionZoneBoundaryModel,
} from "../components/thermalImage/ThermalImage";
import { CurrentProcessedThermalImageDataModel } from "../hooks/useGetProcessedThermalImages";
import {
  ProcessedThermalImages,
  ProcessedThermalImagesZipped,
  ThermalImagesZoneBoundaryInfo,
} from "../types/getProcessedThermalImages";

export interface IThermalImageWorkerInitializeProps {
  offscreenCanvasMain: OffscreenCanvas;
  offscreenCanvasLegends: OffscreenCanvas;
  offscreenCanvasHighlight: OffscreenCanvas;
  temperatureMap: number[][];
  appliedFactor: number;
  infraredMinValue: number;
  infraredMaxValue: number;
  colors: string[] | [number, number, number, number][];
  zoneBoundaryInfo: ThermalImagesZoneBoundaryInfo;
  hotspots?: ThermalHotspot[];
}

export enum ThermalImageWorkerTypes {
  TRANSFER_CANVAS,
  GENERATE_IMAGE,
  HANDLE_MOUSE_HOVER,
  SET_TOOLTIP,
  CLEAR_TOOLTIP,
  CLEAR_HIGHLIGHTED_ZONE,
  GET_PROCESSED_THERMAL_IMAGE_DATA,
  MAX_VALUES,
}

/**
 * @description Worker for thermal image processing will do canvas calculations and drawings in different thread
 * @returns void
 */
const ThermalImageWorker = () => {
  //#region Global Variables
  let _offscreenCanvasMain: OffscreenCanvas;
  let _offscreenCanvasHighlight: OffscreenCanvas;
  let _offscreenCanvasLegends: OffscreenCanvas;
  let _drawedSection: SectionZoneBoundaryModel | null;
  let _sectionZoneBoundaries: SectionZoneBoundaryModel[];
  let _temperatureMap: number[][];
  let _concerningTemp: { x: number; y: number } | undefined;
  let _appliedFactor: number;
  let _colors: string[] | [number, number, number, number][];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let _sectionMaxValues: ISectionMaxValues;
  let _infraredMinValue: number;
  let _infraredMaxValue: number;
  let _zoneBoundaryInfo: ThermalImagesZoneBoundaryInfo;
  let _hotspots: ThermalHotspot[];
  //#endregion Global Variables

  //#region Handle Message
  self.onmessage = (e) => {
    const { type, payload } = e.data;

    if (type === ThermalImageWorkerTypes.TRANSFER_CANVAS) {
      transferCanvas(payload);
    }

    if (type === ThermalImageWorkerTypes.HANDLE_MOUSE_HOVER) {
      handleMouseHover(payload);
    }

    if (type === ThermalImageWorkerTypes.CLEAR_HIGHLIGHTED_ZONE) {
      clearHighlightedZone();
    }

    if (type == ThermalImageWorkerTypes.GENERATE_IMAGE) {
      _concerningTemp = payload.concerningTemp;
      generateImage();
    }

    if (type == ThermalImageWorkerTypes.GET_PROCESSED_THERMAL_IMAGE_DATA) {
      const result = getProcessedThermalImages(payload);

      self.postMessage({
        type: ThermalImageWorkerTypes.GET_PROCESSED_THERMAL_IMAGE_DATA,
        payload: result,
      });
    }
  };
  //#endregion Handle Message

  //#region Initialize
  const transferCanvas = ({
    offscreenCanvasMain,
    offscreenCanvasLegends,
    offscreenCanvasHighlight,
    temperatureMap,
    appliedFactor,
    infraredMinValue,
    infraredMaxValue,
    colors,
    zoneBoundaryInfo,
    hotspots,
  }: IThermalImageWorkerInitializeProps) => {
    _offscreenCanvasMain = offscreenCanvasMain;
    _offscreenCanvasHighlight = offscreenCanvasHighlight;
    _offscreenCanvasLegends = offscreenCanvasLegends;
    _sectionZoneBoundaries = findSectionZoneBoundaries(zoneBoundaryInfo);
    _sectionMaxValues = sectionMaxValues(temperatureMap, zoneBoundaryInfo);
    _temperatureMap = temperatureMap;
    _appliedFactor = appliedFactor;
    _colors = colors;
    _infraredMinValue = infraredMinValue;
    _infraredMaxValue = infraredMaxValue;
    _zoneBoundaryInfo = zoneBoundaryInfo;
    _hotspots = hotspots ?? [];
  };
  //#endregion Initialize

  //#region Drawing
  const generateImage = () => {
    if (
      !_offscreenCanvasMain ||
      !_temperatureMap ||
      _temperatureMap?.length === 0 ||
      !_offscreenCanvasHighlight ||
      !_offscreenCanvasLegends
    )
      return;

    const ctx = _offscreenCanvasMain.getContext("2d");
    if (!ctx) return;

    const numRows = _temperatureMap.length;
    const numCols = _temperatureMap[0]?.length;

    _offscreenCanvasMain.width = numCols;
    _offscreenCanvasMain.height = numRows;

    _offscreenCanvasLegends.width = numCols;
    _offscreenCanvasLegends.height = numRows;

    _offscreenCanvasHighlight.width = numCols;
    _offscreenCanvasHighlight.height = numRows;

    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const thermalValue = _temperatureMap[row][col];
        const color = getColorForThermalValue({
          thermalValue,
        });
        ctx.fillStyle = color;
        ctx.fillRect(col, row, 1, 1);
      }
    }

    drawSectionLines();
    drawHighestTempPoint();
    drawHotspots();
  };

  const handleMouseHover = ({
    mainCanvasRect,
    eventClientX,
    eventClientY,
    computedStyleWidth,
    computedStyleHeight,
  }: {
    mainCanvasRect: DOMRect;
    eventClientX: number;
    eventClientY: number;
    computedStyleWidth: string;
    computedStyleHeight: string;
  }) => {
    if (
      _temperatureMap &&
      _offscreenCanvasMain &&
      computedStyleWidth &&
      computedStyleHeight
    ) {
      const x = eventClientX - mainCanvasRect.left;
      const y = eventClientY - mainCanvasRect.top;
      const numRows = _temperatureMap.length;
      const numCols = _temperatureMap[0]?.length;
      // retrieve current canvas width since it's fuild
      const canvasWidth = parseFloat(computedStyleWidth);
      const canvasHeight = parseFloat(computedStyleHeight);
      const calculatedPixelWidth = canvasWidth / numCols;
      const calculatedPixelHeight = canvasHeight / numRows;
      // calculated position
      const hoverRow = Math.floor(y / calculatedPixelHeight);
      const hoverCol = Math.floor(x / calculatedPixelWidth);
      clearTooltip();
      //COMMENT: Product Backlog Item 13227: Remove zone lines for IR scans in Copilot
      // drawHighlightedZone(_zoneBoundaryInfo, hoverRow, hoverCol);
      if (
        hoverRow >= 0 &&
        hoverRow < numRows &&
        hoverCol >= 0 &&
        hoverCol < numCols
      ) {
        const thermalValue = _temperatureMap[hoverRow][hoverCol];
        if (thermalValue) {
          const positionX = eventClientX - 48;
          const positionY = eventClientY + 10;

          self.postMessage({
            type: ThermalImageWorkerTypes.SET_TOOLTIP,
            payload: {
              positionX,
              positionY,
              thermalValue,
            },
          });
        }
      }
    }
  };

  const drawSectionLines = () => {
    if (!_offscreenCanvasLegends) {
      return;
    }
    const ctx = _offscreenCanvasLegends.getContext("2d");
    if (!ctx) return;

    // COMMENT: Product Backlog Item 13227: Remove zone lines for IR scans in Copilot
    // _zoneBoundaryInfo.HorizontalLines.forEach((section, index) => {
    //   if (index === 0 && section.YStart > 0) {
    //     // Add line at top if section doen't start from the top
    //     ctx.beginPath();
    //     ctx.moveTo(0, section.YStart);
    //     ctx.lineTo(_zoneBoundaryInfo.Width, section.YStart);
    //     ctx.strokeStyle = "white";
    //     ctx.fillStyle = "white";
    //     ctx.lineWidth = 0.7;
    //     ctx.stroke();
    //   }
    //   // Horizontal line
    //   ctx.beginPath();
    //   ctx.moveTo(0, section.YEnd);
    //   ctx.lineTo(_zoneBoundaryInfo.Width, section.YEnd);
    //   ctx.strokeStyle = "white";
    //   ctx.fillStyle = "white";
    //   ctx.lineWidth = 0.7;
    //   ctx.stroke();
    // });
    ctx.font = "14px sans-serif";
    ctx.shadowColor = "white";
    ctx.shadowBlur = 2;
    ctx.lineWidth = 0;
    ctx.fillStyle = "white";
    const textHeight = _offscreenCanvasLegends.height - 8;
    _zoneBoundaryInfo.VerticalLines.forEach((section) => {
      // Vertical line
      ctx.beginPath();
      ctx.moveTo(section.XEnd, 0);
      ctx.lineTo(section.XEnd, _zoneBoundaryInfo.Height);
      //  ctx.strokeStyle = "white";
      ctx.strokeStyle = "white";
      ctx.fillStyle = "white";
      ctx.lineWidth = 0.7;
      ctx.stroke();
      // Clock legend
      const label = `${section.Clock}`;
      const computedX =
        section.XStart +
        Math.floor(
          (section.XEnd - section.XStart) / 2 -
            ctx.measureText(label).width / 2,
        );
      ctx.beginPath();
      ctx.fillStyle = "white";
      ctx.lineWidth = 4;
      ctx.strokeStyle = "black";
      ctx.strokeText(label.toUpperCase(), computedX, textHeight);
      ctx.fillText(label.toUpperCase(), computedX, textHeight);
      ctx.fill();
      ctx.stroke();
    });
  };

  const drawHighestTempPoint = () => {
    const tempHighest = findHighestTempValue(
      _sectionZoneBoundaries,
      _temperatureMap,
    );

    // TODO:
    const highTempCoords = _concerningTemp ?? tempHighest;
    const ctx = _offscreenCanvasMain.getContext("2d");
    if (ctx) {
      const size = 24;
      const centerGap = 6;
      const lineThickness = 2;

      // White border
      ctx.beginPath();
      ctx.lineWidth = lineThickness + 2;
      ctx.strokeStyle = "white";

      // Left horizontal line
      ctx.moveTo(highTempCoords.x - size / 2, highTempCoords.y);
      ctx.lineTo(highTempCoords.x - centerGap, highTempCoords.y);

      // Right horizontal line
      ctx.moveTo(highTempCoords.x + centerGap, highTempCoords.y);
      ctx.lineTo(highTempCoords.x + size / 2, highTempCoords.y);

      // Top vertical line
      ctx.moveTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y - size / 2,
      );
      ctx.lineTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y - centerGap,
      );

      // Bottom vertical line
      ctx.moveTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y + centerGap,
      );
      ctx.lineTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y + size / 2,
      );

      ctx.stroke();

      // Black lines
      ctx.beginPath();
      ctx.lineWidth = lineThickness;
      ctx.strokeStyle = "black";

      // Left horizontal line
      ctx.moveTo(highTempCoords.x - size / 2, highTempCoords.y);
      ctx.lineTo(highTempCoords.x - centerGap, highTempCoords.y);

      // Right horizontal line
      ctx.moveTo(highTempCoords.x + centerGap, highTempCoords.y);
      ctx.lineTo(highTempCoords.x + size / 2, highTempCoords.y);

      // Top vertical line
      ctx.moveTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y - size / 2,
      );
      ctx.lineTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y - centerGap,
      );

      // Bottom vertical line
      ctx.moveTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y + centerGap,
      );
      ctx.lineTo(
        highTempCoords.x + lineThickness / 2,
        highTempCoords.y + size / 2,
      );

      ctx.stroke();
    }
  };

  const drawHotspots = () => {
    if (!_offscreenCanvasMain || _hotspots.length === 0 || !_zoneBoundaryInfo)
      return;

    const ctx = _offscreenCanvasMain.getContext("2d");
    if (!ctx) return;

    ctx.strokeStyle = "red";
    ctx.lineWidth = 2;

    _hotspots?.forEach((hotspot) => {
      const padding = 5;

      const x = Math.max(0, hotspot.MinX - padding);
      const y = Math.max(0, hotspot.MinY - padding);
      const width = Math.min(
        _zoneBoundaryInfo.Width - x,
        hotspot.MaxX - hotspot.MinX + padding * 2,
      );
      const height = Math.min(
        _zoneBoundaryInfo.Height - y,
        hotspot.MaxY - hotspot.MinY + padding * 2,
      );

      ctx.beginPath();
      ctx.strokeRect(x, y, width, height);
      ctx.stroke();
    });
  };

  /**
   * @deprecated Product Backlog Item 13227: Remove zone lines for IR scans in Copilot
   */
  // const drawHighlightedZone = (
  //   zoneBoundaryInfo: ThermalImagesZoneBoundaryInfo,
  //   row: number,
  //   col: number
  // ) => {
  //   const ctx = _offscreenCanvasHighlight.getContext("2d");
  //   const sectionZoneBoundaries = findSectionZoneBoundaries(zoneBoundaryInfo);
  //   if (ctx && sectionZoneBoundaries.length) {
  //     const section = sectionZoneBoundaries.find(
  //       (x) =>
  //         x.StartPoint.Y <= row &&
  //         row <= x.EndPoint.Y &&
  //         x.StartPoint.X <= col &&
  //         col <= x.EndPoint.X
  //     );
  //     if (
  //       section &&
  //       (!_drawedSection ||
  //         _drawedSection.ZoneName != section.ZoneName ||
  //         _drawedSection.Clock != section.Clock)
  //     ) {
  //       ctx.beginPath();
  //       ctx.strokeStyle = "red";
  //       ctx.lineWidth = 1;
  //       ctx.fillStyle = "red";
  //       ctx.strokeRect(
  //         section.StartPoint.X,
  //         section.StartPoint.Y,
  //         section.EndPoint.X - section.StartPoint.X,
  //         section.EndPoint.Y - section.StartPoint.Y
  //       );
  //       ctx.stroke();
  //       drawSectionHighestTempPoint(section.ZoneName, section.Clock);
  //       _drawedSection = section;
  //     } else if (!section) {
  //       clearHighlightedZone();
  //     }
  //   }
  // };

  /**
   * @deprecated Product Backlog Item 13227: Remove zone lines for IR scans in Copilot
   */
  // const drawSectionHighestTempPoint = (zoneName: string, clock: number) => {
  //   const ctx = _offscreenCanvasHighlight.getContext("2d");

  //   const sectionKey = `${zoneName};${clock}`;
  //   if (ctx) {
  //     if (
  //       _sectionMaxValues &&
  //       _sectionMaxValues[sectionKey] &&
  //       _sectionMaxValues[sectionKey].x &&
  //       _sectionMaxValues[sectionKey].y
  //     ) {
  //       ctx.beginPath();
  //       ctx.lineWidth = 2;
  //       ctx.strokeStyle = "black";
  //       ctx.fillStyle = "transparent";
  //       const radius = 4;
  //       ctx.arc(
  //         _sectionMaxValues[sectionKey].y + 0.5,
  //         _sectionMaxValues[sectionKey].x + 0.5,
  //         radius,
  //         0,
  //         2 * Math.PI,
  //         false
  //       );
  //       ctx.fill();
  //       ctx.stroke();
  //     }
  //   }
  // };
  //#endregion Drawing

  //#region Clear Drawings
  const clearTooltip = () => {
    self.postMessage({
      type: ThermalImageWorkerTypes.CLEAR_TOOLTIP,
      payload: null,
    });
  };

  const clearHighlightedZone = () => {
    if (_drawedSection) {
      const ctx = _offscreenCanvasHighlight.getContext("2d");
      if (ctx) {
        ctx.beginPath();
        ctx.clearRect(
          0,
          0,
          _offscreenCanvasHighlight.width ?? 0,
          _offscreenCanvasHighlight.height ?? 0,
        );
        ctx.stroke();
      }
      _drawedSection = null;
    }
  };
  //#endregion Clear Drawings

  //#region Find Values
  const getProcessedThermalImages = ({
    zippedImagesResult,
    pakoOutput,
    IRImageType,
    allHotspots,
  }: {
    zippedImagesResult: ProcessedThermalImagesZipped;
    pakoOutput: string;
    IRImageType: {
      East: number;
      West: number;
      North: number;
    };
    allHotspots?: ThermalHotspot[];
  }): CurrentProcessedThermalImageDataModel | undefined => {
    if (!zippedImagesResult) return;

    const newDataImages: ProcessedThermalImages[] = [];

    if (pakoOutput) {
      const parsedData = JSON.parse(pakoOutput) as ProcessedThermalImages[];

      newDataImages.push(
        ...parsedData.map((x) => {
          const thermalCalibration =
            zippedImagesResult.thermalCalibrations?.find(
              (y) => y.frameLocation === x.FrameLocation,
            );

          const concerningTemp = zippedImagesResult.concerningTemps?.find(
            (y) => y.frameLocation === x.FrameLocation,
          );

          return {
            ...x,
            StartDate: zippedImagesResult.startTimeUtc ?? "",
            thermalCalibration: {
              factor: thermalCalibration?.factor ?? 1,
              message: thermalCalibration?.message ?? "",
            },
            concerningTemp: concerningTemp
              ? {
                  x: concerningTemp.x,
                  y: concerningTemp.y,
                  temp: concerningTemp.temp,
                }
              : undefined,
            Hotspots: allHotspots?.filter(
              (y) => y.FrameLocation === x.FrameLocation,
            ),
          };
        }),
      );
    }

    const east = newDataImages.find(
      (x) => x.FrameLocation === IRImageType.East,
    ) as ProcessedThermalImages;
    const west = newDataImages.find(
      (x) => x.FrameLocation === IRImageType.West,
    ) as ProcessedThermalImages;
    const north = newDataImages.find(
      (x) => x.FrameLocation === IRImageType.North,
    ) as ProcessedThermalImages;

    return {
      east,
      west,
      north,
    } as CurrentProcessedThermalImageDataModel;
  };

  const sectionMaxValues = (
    temperatureMap: number[][],
    zoneBoundaryInfo: ThermalImagesZoneBoundaryInfo,
  ): ISectionMaxValues => {
    let maxValues: ISectionMaxValues = {};
    zoneBoundaryInfo?.HorizontalLines?.forEach((horizontalCoord) => {
      const horizontalFiltered = temperatureMap.slice(
        horizontalCoord.YStart,
        horizontalCoord.YEnd,
      );
      zoneBoundaryInfo?.VerticalLines?.forEach((verticalCoord) => {
        const filteredSectionTemperatures = horizontalFiltered.map((row) =>
          row.slice(verticalCoord.XStart, verticalCoord.XEnd),
        );

        const max = Math.max(...filteredSectionTemperatures.flat());

        maxValues = {
          ...maxValues,
          [`${horizontalCoord.ZoneName};${verticalCoord.Clock}`]: {
            ...(findTemperatureDataInSectionByValue(
              temperatureMap,
              max,
              horizontalCoord.YStart,
              horizontalCoord.YEnd,
              verticalCoord.XStart,
              verticalCoord.XEnd,
            ) ?? {}),
          },
        } as ISectionMaxValues;
      });
    });

    self.postMessage({
      type: ThermalImageWorkerTypes.MAX_VALUES,
      payload: maxValues,
    });

    return maxValues;
  };

  const findTemperatureDataInSectionByValue = (
    temperatureMap: number[][],
    value: number,
    xStart: number,
    xEnd: number,
    yStart: number,
    yEnd: number,
  ) => {
    let response;
    temperatureMap.forEach((rows, x) => {
      rows.find((thermalValue, y) => {
        if (
          thermalValue == value &&
          x >= xStart &&
          x < xEnd &&
          y >= yStart &&
          y < yEnd
        ) {
          response = {
            x,
            y,
            val: thermalValue,
          };
        }
      });
    });

    return response;
  };

  const findSectionZoneBoundaries = (
    zoneBoundaryInfo: ThermalImagesZoneBoundaryInfo,
  ) => {
    return zoneBoundaryInfo?.HorizontalLines?.flatMap((h) =>
      zoneBoundaryInfo?.VerticalLines?.map((v) => {
        return {
          ZoneName: h.ZoneName,
          Clock: v.Clock,
          StartPoint: { X: v.XStart, Y: h.YStart },
          EndPoint: { X: v.XEnd, Y: h.YEnd },
        } as SectionZoneBoundaryModel;
      }),
    );
  };

  const findHighestTempValue = (
    sectionZoneBoundaries: SectionZoneBoundaryModel[],
    temperatureMap: number[][],
  ) => {
    let highTempCoords: { x: number; y: number } = {
      x: Number.NEGATIVE_INFINITY,
      y: Number.NEGATIVE_INFINITY,
    };
    let highTemp: number = Number.NEGATIVE_INFINITY;

    temperatureMap?.forEach((rows, row) => {
      rows.forEach((thermalValue, col) => {
        if (
          sectionZoneBoundaries?.some(
            (x) =>
              x.StartPoint.Y <= row &&
              row <= x.EndPoint.Y &&
              x.StartPoint.X <= col &&
              col <= x.EndPoint.X,
          ) &&
          thermalValue > highTemp
        ) {
          highTemp = thermalValue;
          highTempCoords = { x: col, y: row };
        }
      });
    });

    return highTempCoords;
  };

  const getColorForThermalValue = ({
    thermalValue,
  }: {
    thermalValue: number;
  }): string => {
    const minValue = _infraredMinValue;
    const maxValue = _infraredMaxValue;

    const limitValue =
      thermalValue < minValue
        ? minValue
        : thermalValue > maxValue
          ? maxValue
          : thermalValue;

    const normalizedValue =
      (limitValue * _appliedFactor - minValue) / (maxValue - minValue);

    const colorIndex = Math.floor(normalizedValue * (_colors.length - 1));

    return _colors[colorIndex] as string;
  };
  //#endregion Find Values
};

export default ThermalImageWorker;
